import React, { useState } from 'react';
import Hero from '../../../components/Hero/Hero';
import Pathfinder from '../../../components/Pathfinder/Pathfinder';
import Metadata from '../../../components/Metadata/Metadata';
import Section from '../../../components/Section/Section';
import Card from '../../../components/Card/Card';
import Wallpaper from '../../../components/Wallpaper/Wallpaper';
import GlobalCallout from '../../../components/Global/CalloutDefault';
import { Box, Typography } from '@mui/material';
import Accordion from '../../../components/Accordion/Accordion';
import Button from '../../../components/Button/Button';
import Image from '../../../components/Image/Image';
import ContentSplit from '../../../components/ContentSplit/ContentSplit';
import ListItem from '../../../components/ListItem/ListItem';
import { Link } from 'react-router-dom';

const PageContent: React.FC = () => {
  const [expandAll, setExpandAll] = useState(false);

  const toggleAll = () => {
    setExpandAll(!expandAll);
  };

  return (
    <Box>
      <Metadata
        title="National Security Solutions | General Genomics"
        description="Discover how General Genomics supports national defense with innovative data solutions. Explore bioinformatics, predictive analytics, and cybersecurity for enhanced national security operations."
      />
      <Hero
        helper="Industries &gt; Government"
        headline="Optimizing national security through <span class='nowrap'>AI-powered insights and solutions</span>"
      />

      <Section theme="" className="cards-large-icons">
        <h2 className="action-on-view action-fade-in">
          Revolutionizing National Security with Data
        </h2>
        <p className="action-on-view action-fade-in">
          At General Genomics, we specialize in providing cutting-edge data
          solutions that support national defense operations. Our offerings
          range from bioinformatics and predictive analytics to cybersecurity
          and biological threat detection. With a focus on security,
          scalability, and innovation, we help defense agencies safeguard
          against evolving threats while optimizing their data management
          processes. Explore how we can empower your operations with AI-driven
          insights and state-of-the-art data solutions tailored for national
          security challenges.
        </p>
        <Image
          path="/images/library/soldiers-running.webp"
          size="wide"
          className="action-on-view action-fade-in"
          alt="This is a wide image."
        />
      </Section>

      <ContentSplit
        image="/images/library/military-office-2.webp"
        position="right"
      >
        <p className="helper action-on-view action-fade-in">Our Expertise</p>
        <h2 className="action-on-view action-fade-in">
          Secure Data Solutions for National Defense
        </h2>
        <ListItem
          icon="check"
          iconClass="highlighted-icon"
          className="custom-list-item"
        >
          <p>
            <strong>Cybersecurity Excellence:</strong> Our solutions safeguard
            critical data by providing proactive and reactive measures against
            evolving cybersecurity threats in national defense.
          </p>
        </ListItem>
        <ListItem
          icon="check"
          iconClass="highlighted-icon"
          className="custom-list-item"
        >
          <p>
            <strong>AI-Powered Insights:</strong> Harness the power of
            artificial intelligence to gain actionable insights, optimize
            defense strategies, and predict emerging threats in real-time.
          </p>
        </ListItem>
        <ListItem
          icon="check"
          iconClass="highlighted-icon"
          className="custom-list-item"
        >
          <p>
            <strong>Bioinformatics for Defense:</strong> Leverage advanced
            bioinformatics to detect and respond to biological threats, ensuring
            proactive national defense preparedness.
          </p>
        </ListItem>
        <ListItem
          icon="check"
          iconClass="highlighted-icon"
          className="custom-list-item"
        >
          <p>
            <strong>Scalable Solutions:</strong> Our solutions are designed to
            scale seamlessly, ensuring that your data infrastructure can handle
            growing defense demands efficiently.
          </p>
        </ListItem>
      </ContentSplit>

      <Section theme="dark" className="section-cross-linking">
        <h2 className="action-on-view action-fade-in">Explore Our Solutions</h2>
        <p className="action-on-view action-fade-in">
          Discover how General Genomics delivers cutting-edge solutions for
          secure and innovative defense operations.
        </p>
        <div className="card-group">
          <Card
            icon="security"
            headline="Data Governance"
            subhead="Ensure strict compliance with advanced security protocols for data management."
            btnURL="/our-approach/data-governance-and-compliance"
            btnLabel="Learn more"
            className="action-on-view action-fade-in"
          />
          <Card
            icon="lightbulb"
            headline="Innovation"
            subhead="Explore AI-driven advancements revolutionizing national security solutions."
            btnURL="/innovation"
            btnLabel="Learn more"
            className="action-on-view action-fade-in"
          />
          <Card
            icon="cloud"
            headline="Data Portal"
            subhead="Access cutting-edge data solutions designed for defense operations."
            btnURL="/data-portal"
            btnLabel="Learn more"
            className="action-on-view action-fade-in"
          />
        </div>
      </Section>

      <Wallpaper asset="../../images/library/lab-scientist-military-4.webp"></Wallpaper>

      <Section>
        <Typography variant="h2" className="action-on-view action-fade-in">
          Frequently asked questions about General Genomics&apos; National
          Security Solutions
        </Typography>
        <Typography className="action-on-view action-fade-in">
          Learn about General Genomics&apos; cutting-edge data solutions for
          national security, including bioinformatics, cybersecurity, and threat
          detection.
        </Typography>
        <Box className="accordion-wrapper action-on-view action-fade-in">
          <Button
            className="accordion-button"
            type="outlined"
            onClick={toggleAll}
            icon={expandAll ? 'expand_less' : 'expand_more'}
            label={expandAll ? 'Collapse All' : 'Expand All'}
          />
          <Accordion
            isOpen={expandAll}
            headline="What data solutions does General Genomics offer for national security?"
          >
            General Genomics provides cutting-edge solutions in{' '}
            <Link to="/industries/government/bioinformatics">
              bioinformatics,
            </Link>{' '}
            predictive analytics, cybersecurity, and biological threat
            detection, all designed to support national defense operations with
            secure, scalable, and innovative data management.
          </Accordion>
          <Accordion
            isOpen={expandAll}
            headline="How can General Genomics' solutions improve national security defense strategies?"
          >
            Our AI-driven insights help both proactive and reactive defense
            strategies by offering real-time data analysis, enhancing
            decision-making, and improving threat detection and response
            capabilities.
          </Accordion>
          <Accordion
            isOpen={expandAll}
            headline="Does General Genomics have experience in national security applications?"
          >
            Yes, we have demonstrated success in real-world applications,
            including securing national defense data, protecting critical
            infrastructures, and preventing biological threats through our
            innovative data solutions.
          </Accordion>
          <Accordion
            isOpen={expandAll}
            headline="How does General Genomics ensure the security and compliance of its data solutions?"
          >
            We adhere to strict data governance and compliance protocols to
            ensure that our solutions meet national security standards and are
            equipped to handle sensitive data securely.
          </Accordion>
          <Accordion
            isOpen={expandAll}
            headline="How can I learn more or get assistance with General Genomics' national security solutions?"
          >
            You can explore our offerings further by requesting a demo or
            contacting our team directly to discuss how we can support your
            national security needs.
          </Accordion>
        </Box>
      </Section>

      <Section theme="dark" bgName="lines" className="section-call-to-action">
        <Typography variant="h2" className="action-on-view action-fade-in">
          Why partnering with General Genomics transforms bioinformatics
          research and innovation
        </Typography>
        <Box className="card-group">
          <Card
            icon="check"
            headline="Advanced Solutions in Analytics"
            subhead="General Genomics combines analytics and innovation with cutting-edge algorithms, large language models, machine learning, and predictive analytics to transform synthetic systems and complex data insights."
            className="action-on-view action-fade-in"
          />
          <Card
            icon="check"
            headline="Precision Insights"
            subhead="Harness predictive modeling and genomic data analysis to drive accurate, actionable insights tailored to research and healthcare objectives."
            className="action-on-view action-fade-in action-delay-250"
          />
          <Card
            icon="check"
            headline="Collaborative Expertise"
            subhead="Partner with a team experienced in supporting government initiatives to advance bioinformatics research and public health strategies."
            className="action-on-view action-fade-in action-delay-500"
          />
        </Box>
      </Section>

      <GlobalCallout />

      <Pathfinder />
    </Box>
  );
};

export default PageContent;
